import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'

import { ClerkProvider } from '@clerk/react'
import { PostHogProvider } from '@posthog/react'
import posthog from 'posthog-js'

import { persistOptions, queryClient } from '@/lib/react-query'

import { AppLoaderProvider } from '@/features/app-shell/app-loader-context'

import { env } from './env'
import { isDemoPresentation, isDEV } from './lib/constants'

import type { PropsWithChildren } from 'react'

// Analytics stay on in every mode — including the demo — against the env's own
// PostHog project (ADR 0006 dec.6: Sentry is skipped in mocked builds, PostHog
// is not).
//
// Which key/host the demo gets depends on WHO built it, and both halves matter:
// `terraform/build.tf` derives `.env.demo.deploy` from the committed
// `.env.demo` at deploy time, swapping in the real key/host — so ONLY the
// deployed demo can report. Local `build:demo` and CI runs keep `.env.demo`'s
// unroutable `.invalid` PostHog host, so they physically cannot emit into the
// shared project — that keeps CI/local noise out of the demo-engagement
// analytics (the point of `demo_mode` below) and holds the off-network e2e
// invariant. Rationale + the repo-wide env rule: knowledge/env-files.md.
// Either way the CSP `connect-src` is built from `VITE_POSTHOG_HOST` (dec.9a),
// covering the exit-flush the Service Worker can't intercept.
posthog.init(env.VITE_POSTHOG_KEY, {
	api_host: env.VITE_POSTHOG_HOST,
	person_profiles: 'identified_only',
	defaults: '2025-11-30',

	// SPA don't track page changes by default, needs to be manually tracked
	capture_pageview: false,
	capture_pageleave: true,

	// Disable unecessary capturing
	disable_external_dependency_loading: true,
	disable_session_recording: true,
	disable_web_experiments: true,
	disable_surveys: true,
	advanced_disable_feature_flags: true,
})

// Every demo event carries `demo_mode: true` (ADR 0015 dec. 7 · ADR 0006 dec. 6
// as amended). A persisted super property, not a per-event payload, and set at
// module scope so it is already registered before the first `$pageview` fires
// from `src/pageview.tsx` — demo traffic is separable in the shared project
// without a second project or a second key.
if (isDemoPresentation) {
	posthog.register({ demo_mode: true })
}

// Opt out of capturing in development (covers `dev`, `dev:mock`, `dev:demo`).
if (isDEV) {
	posthog.opt_out_capturing()
}

/**
 * Root provider tree: Clerk (auth) → PersistQueryClient (cached server state)
 * → PostHog (analytics). Note `posthog.init` runs above at module-import time
 * as a side-effect, so the SDK is configured before this tree mounts.
 */
export const Providers = ({ children }: PropsWithChildren) => {
	return (
		<AppLoaderProvider>
			<ClerkProvider
				publishableKey={env.VITE_CLERK_PUBLISHABLE_KEY}
				prefetchUI={false}
			>
				<PersistQueryClientProvider
					client={queryClient}
					persistOptions={persistOptions}
				>
					<PostHogProvider client={posthog}>{children}</PostHogProvider>
				</PersistQueryClientProvider>
			</ClerkProvider>
		</AppLoaderProvider>
	)
}
